import { useCallback, useEffect, useState } from "react";
import { Loader2, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ABSENCE_TYPES } from "@/lib/format";
import { StatusBadge } from "@/components/Brand";
import { absenceSpan, requestedAt } from "@/pages/admin/AdminAbsences";
import { TechOvertime } from "@/components/TechOvertime";

const EMPTY = { type: "permesso", start_date: "", end_date: "", start_time: "", end_time: "", reason: "" };

export default function TechAbsences() {
  const [f, setF] = useState(EMPTY);
  const [items, setItems] = useState([]);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/absences").then((r) => setItems(r.data)), []);
  useEffect(() => {
    load();
    api.get("/public/config").then((r) => setNotice(r.data.absence_notice_hours));
  }, [load]);
  const upd = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const partial = f.type === "permesso";

  const submit = async () => {
    if (!f.start_date) return toast.error("Inserisci la data di inizio");
    if (partial && (!f.start_time || !f.end_time)) return toast.error("Indica l'orario del permesso");
    setBusy(true);
    try {
      await api.post("/absences", {
        type: f.type, start_date: f.start_date, end_date: partial ? f.start_date : f.end_date || f.start_date,
        start_time: partial ? f.start_time : null, end_time: partial ? f.end_time : null, reason: f.reason || null,
      });
      toast.success("Richiesta inviata all'amministratore");
      setF(EMPTY);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rise">
        <p className="text-[11px] font-mono uppercase tracking-[0.2em] text-[#61a229]">Gestione assenze</p>
        <h1 className="mt-1 font-display text-3xl font-bold">Richiedi un permesso</h1>
      </div>
      <TechOvertime />
      <section className="space-y-4 rounded-2xl border bg-white p-4" data-testid="absence-form">
        {notice != null && <p className="flex gap-2 rounded-xl bg-[#a2ca6b]/15 p-3 text-xs text-[#2f5212]" data-testid="absence-notice-hint">
          <Info className="h-4 w-4 shrink-0" /> Preavviso minimo: {notice} ore (non richiesto per malattia).</p>}
        <div className="space-y-2"><Label>Tipo</Label>
          <Select value={f.type} onValueChange={(v) => setF((p) => ({ ...p, type: v }))}>
            <SelectTrigger data-testid="absence-type-select"><SelectValue /></SelectTrigger>
            <SelectContent>{Object.entries(ABSENCE_TYPES).filter(([k]) => k !== "ferie").map(([k, l]) => <SelectItem key={k} value={k} data-testid={`absence-type-${k}`}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2 col-span-2 sm:col-span-1"><Label>{partial ? "Giorno" : "Dal"}</Label><Input type="date" value={f.start_date} onChange={upd("start_date")} data-testid="absence-start-date" /></div>
          {partial ? (
            <>
              <div className="space-y-2"><Label>Dalle</Label><Input type="time" value={f.start_time} onChange={upd("start_time")} data-testid="absence-start-time" /></div>
              <div className="space-y-2"><Label>Alle</Label><Input type="time" value={f.end_time} onChange={upd("end_time")} data-testid="absence-end-time" /></div>
            </>
          ) : (
            <div className="space-y-2 col-span-2 sm:col-span-1"><Label>Al</Label><Input type="date" value={f.end_date} onChange={upd("end_date")} data-testid="absence-end-date" /></div>
          )}
        </div>
        <div className="space-y-2"><Label>Motivazione</Label><Textarea rows={2} value={f.reason} onChange={upd("reason")} data-testid="absence-reason" /></div>
        <Button onClick={submit} disabled={busy} className="w-full rounded-full h-11" data-testid="absence-submit-button">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Invia richiesta</Button>
      </section>
      <section className="space-y-2">
        <p className="text-sm font-semibold">Le mie richieste</p>
        {items.map((a, i) => (
          <div key={a.id} className="flex items-center justify-between rounded-xl border bg-white p-3" data-testid={`my-absence-${i}`}>
            <div><p className="text-sm font-medium">{ABSENCE_TYPES[a.type]}</p><p className="font-mono text-[11px] text-slate-500">{absenceSpan(a)}</p>{a.created_at && <p className="text-[11px] text-slate-400" data-testid={`my-absence-requested-${i}`}>{requestedAt(a)}</p>}</div>
            <StatusBadge status={a.status} />
          </div>
        ))}
        {!items.length && <p className="text-sm text-slate-500" data-testid="my-absences-empty">Nessuna richiesta.</p>}
      </section>
    </div>
  );
}
